"use client";

import { DEFAULT_ANCHORS, Eligibility, TIER_SLATE, TrialDto } from "@decibeltrade/sdk";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";

import { isProduction } from "@/env";

/**
 * Dev-only state override for the FFT two-pane dashboard.
 *
 * Usage: `/rewards/first-trade-on-us?fftState=<base64-json>` or the shorthand
 * `?fftState=lock|trial|active|settled` for the four step states.
 *
 * Mirrors the cardvault override pattern. Short-circuits to `null` in
 * production builds. Lets Playwright tests inject any step state without
 * needing a real wallet, real on-chain campaign, or a running trading-api.
 */

const OVERRIDE_PARAM = "fftState";

export type WizardStep = "lock" | "trial" | "active" | "settled";

export interface FftDashboardOverride {
  /** Force a specific step. If unset, derive from eligibility + activeTrial. */
  step?: WizardStep;
  /** Override the synthesized eligibility. All fields optional — defaults below. */
  eligibility?: Partial<Eligibility>;
  /** Override the active trial (or null to assert no active trial). */
  activeTrial?: TrialDto | null;
  /** Override the user's USDC balance (display units). */
  usdcBalance?: number;
  /** Override the primary-subaccount withdrawable balance (display units). */
  subaccountAvailable?: number;
}

const SHORTHANDS: Record<string, FftDashboardOverride> = {
  lock: {
    step: "lock",
    eligibility: {
      activeLockCount: 0,
      activeLockedAmount: BigInt(0),
      creditsGranted: 0,
      activeLockSubaccount: null,
    },
    activeTrial: null,
  },
  trial: {
    step: "trial",
    eligibility: { activeLockCount: 1, creditsGranted: 3, creditsUsed: 0 },
    activeTrial: null,
  },
  active: {
    step: "active",
    activeTrial: buildSyntheticTrial("active"),
  },
  settled: {
    step: "settled",
    activeTrial: buildSyntheticTrial("settled-win"),
  },
};

function decode(raw: string | null): FftDashboardOverride | null {
  if (!raw) return null;
  if (raw in SHORTHANDS) return SHORTHANDS[raw] ?? null;
  try {
    const json = typeof atob === "function" ? atob(raw) : Buffer.from(raw, "base64").toString();
    return JSON.parse(json) as FftDashboardOverride;
  } catch {
    return null;
  }
}

export function useFftDashboardOverride(): FftDashboardOverride | null {
  const searchParams = useSearchParams();
  const urlValue = searchParams.get(OVERRIDE_PARAM);

  return useMemo(() => {
    if (isProduction) return null;
    return decode(urlValue);
  }, [urlValue]);
}

/**
 * Build a complete Eligibility from a partial override + safe defaults.
 *
 * Defaults are tuned so the user is eligible to open a trial with 3 credits
 * at the $250 tier × 7d.
 */
export function buildSyntheticEligibility(partial: Partial<Eligibility> | undefined): Eligibility {
  const base: Eligibility = {
    activeLockedAmount: BigInt(250_000_000),
    maxActiveDurationDays: 7,
    creditsGranted: 3,
    creditsUsed: 0,
    activeLockCount: 1,
    activeLockUnlockAtMs: Date.now() + 7 * 24 * 3600 * 1000,
    activeLockId: BigInt(1),
    activeLockSubaccount: "0x88736b302257e9c359fe3c48519f49e60b630055499294a3bb36b787ba57ada2",
    campaignTitle: "First Trade on Us",
    marketAddr: "0xbtc",
    minLockAmount: BigInt(250_000_000),
    expiryMs: 60_000,
    payoutAnchors: DEFAULT_ANCHORS,
    tierSlate: [...TIER_SLATE],
    trialsPaused: false,
    locksPaused: false,
    allTrialsFrozen: false,
    dailyBurn: {
      windowTotal: BigInt(0),
      cap: BigInt(148_000_000),
      liveReservationCount: 0,
      projectedAfterTrial: BigInt(10_000_000),
    },
    oiState: {
      totalNotional: BigInt(0),
      cap: BigInt(50_000_000_000),
    },
    canOpenTrial: true,
    blockers: [],
    blockerCodes: [],
    relockDisabled: false,
    hasEverBeenGranted: false,
    softWarnings: { dailyBurnNearCap: false },
  };
  if (!partial) return base;
  const merged = {
    ...base,
    ...partial,
    dailyBurn: { ...base.dailyBurn, ...partial.dailyBurn },
    oiState: { ...base.oiState, ...partial.oiState },
    softWarnings: { ...base.softWarnings, ...partial.softWarnings },
  };
  // Unless overridden, derive the gate like getEligibility does so synthetic
  // states stay self-consistent (e.g. exhausted credits => can't open).
  if (partial.canOpenTrial === undefined) {
    merged.canOpenTrial =
      !merged.trialsPaused &&
      !merged.allTrialsFrozen &&
      merged.creditsGranted - merged.creditsUsed > 0 &&
      merged.activeLockedAmount >= merged.minLockAmount;
  }
  return merged;
}

export function buildSyntheticTrial(
  kind: "active" | "settled-win" | "settled-loss" | "settled-admin-reset",
): TrialDto {
  const now = Date.now();
  const base: TrialDto = {
    trial_id: 1,
    user: "0xtest",
    campaign_addr: "0x4e110",
    market: "0xbtc",
    side: "Buy",
    protected_amount: 10_000_000,
    protected_amount_usd: 10,
    // $10 protected × 40x (7d tier) = $400 notional at the $60k mark, as a normalized size.
    size: 0.006667,
    mark_at_open: 60_000_000_000,
    mark_at_open_usd: 60_000,
    trial_subaccount: "0xtrialsub",
    opened_at_ms: now - 30_000,
    status: "Active",
  };
  switch (kind) {
    case "active":
      return { ...base, expires_at_ms: now + 30_000 };
    case "settled-win":
      return {
        ...base,
        status: "Settled",
        closed_at_ms: now,
        user_payout: 4_270_000,
        user_payout_usd: 4.27,
        vault_returned: 10_000_000,
        settle_reason: "ExpiredClean",
      };
    case "settled-loss":
      return {
        ...base,
        status: "SettledLiquidated",
        closed_at_ms: now,
        user_payout: 0,
        user_payout_usd: 0,
        vault_returned: 0,
        settle_reason: "LiquidatedEmpty",
      };
    case "settled-admin-reset":
      return {
        ...base,
        status: "Settled",
        closed_at_ms: now,
        user_payout: 0,
        user_payout_usd: 0,
        settle_reason: "AdminReset",
        prior_status: "Active",
      };
  }
}
